import type { Icon } from "@phosphor-icons/react";
import { DOCS_URL, HUNT_HUB_URL, STATUS_URL } from "@/lib/links";
import {
  Database,
  Desktop,
  Crosshair,
  ListChecks,
  Code,
  Graph,
  Compass,
  Detective,
  Bell,
  Siren,
  GitBranch,
  Buildings,
} from "@phosphor-icons/react";

/**
 * The single source of truth for the Platform and Solutions information
 * architecture. Navbar, Footer, the homepage capability index, the platform
 * overview and the solution pages all read from here, so a renamed capability
 * changes everywhere at once.
 *
 * Adding an entry here still requires the route in src/App.tsx, the sitemap
 * and ROUTE_META (see CLAUDE.md) — `npm run check-routes` will tell you.
 */

export interface NavItem {
  slug: string;
  href: string;
  label: string;
  /** One line, under ten words. Shown in menus and index rows. */
  short: string;
  icon: Icon;
}

export const PLATFORM_ITEMS: NavItem[] = [
  {
    slug: "federated-siem",
    href: "/platform/federated-siem",
    label: "Federated SIEM",
    short: "Your logs, your lake or ours. One query.",
    icon: Database,
  },
  {
    slug: "endpoint-control",
    href: "/platform/endpoint-control",
    label: "Endpoint Control",
    short: "Live answers from every host.",
    icon: Desktop,
  },
  {
    slug: "threat-hunting",
    href: "/platform/threat-hunting",
    label: "Threat Hunting",
    short: "Hypotheses, checkpoints, playbooks.",
    icon: Crosshair,
  },
  {
    slug: "alert-triage",
    href: "/platform/alert-triage",
    label: "AI Alert Triage",
    short: "Every detection pre-read by Scout.",
    icon: ListChecks,
  },
  {
    slug: "detection-engineering",
    href: "/platform/detection-engineering",
    label: "Detection Engineering",
    short: "Sigma you write, test and version.",
    icon: Code,
  },
  {
    slug: "threat-intelligence",
    href: "/platform/threat-intelligence",
    label: "Threat Intelligence",
    short: "STIX in, one graph that remembers.",
    icon: Graph,
  },
];

export const SCOUT_ITEM: NavItem = {
  slug: "scout",
  href: "/platform/scout",
  label: "Scout",
  short: "An AI partner you can trust.",
  icon: Compass,
};

export const SOLUTION_ITEMS: NavItem[] = [
  {
    slug: "threat-hunters",
    href: "/solutions/threat-hunters",
    label: "For threat hunters",
    short: "Hypotheses, checkpoints, playbooks you can export.",
    icon: Detective,
  },
  {
    slug: "soc",
    href: "/solutions/soc",
    label: "For SOC analysts",
    short: "Every detection pre-read, evidence attached.",
    icon: Bell,
  },
  {
    slug: "incident-response",
    href: "/solutions/incident-response",
    label: "For IR teams",
    short: "Stored history and live endpoint reach, one workspace.",
    icon: Siren,
  },
  {
    slug: "detection-engineering",
    href: "/solutions/detection-engineering",
    label: "For detection engineers",
    short: "Sigma, backtests, shadow mode, version history.",
    icon: GitBranch,
  },
  {
    slug: "mssp",
    href: "/solutions/mssp",
    label: "For MSSPs",
    short: "One Library, deployed across every organisation you run.",
    icon: Buildings,
  },
];

export interface LinkItem {
  href: string;
  label: string;
  /** Another property (docs, Hub, status): opens in a new tab. */
  external?: boolean;
}

export const RESOURCE_ITEMS: LinkItem[] = [
  { href: "/demo", label: "Watch Scout hunt" },
  { href: DOCS_URL, label: "Docs", external: true },
  // The Huntbase Hub is its own property, not a section of this site.
  { href: HUNT_HUB_URL, label: "Huntbase Hub", external: true },
  { href: "/blog", label: "Blog" },
  { href: "/glossary", label: "Glossary" },
  { href: "/trust-center", label: "Trust Center" },
  { href: STATUS_URL, label: "Status", external: true },
];

export const platformItem = (slug: string): NavItem =>
  [...PLATFORM_ITEMS, SCOUT_ITEM].find((i) => i.slug === slug) ?? SCOUT_ITEM;
